import { useCallback, useEffect, useMemo, useState } from "react";
import Domain from "../../services/domain";
import Toolbar from "../controls/Toolbar";
import PubSub from "../../services/PubSub";
import { useTranslation } from "react-i18next";
import { useGetUser, useHasPermission } from "../../common/Hooks";
import Toggle from "../controls/Toggle";
import SettingsView from "../settings/SettingsView";
import Utils from "../../utils/utils";
import { Form, Formik } from "formik";
import TextBox from "../controls/form/TextBox";

const DomainDetails = ({ id }) => {
    const [translate] = useTranslation();
    const [domain, setDomain] = useState({});
    const [products, setProducts] = useState([]);
    const [showSaveButton, setShowSaveButton] = useState(false);

    const [user] = useGetUser();
    const permissions = useHasPermission({
        domain: id,
        environment: null,
        deployment: null,
        type: "domain"
    });

    const [settingsSetId, setSettingsSetId] = useState("");

    const getDomain = useCallback(async (id) => {
        const response = await Domain.getDomain(id);
        if (!response.error) {
            setDomain(response.data);
            setSettingsSetId(response.data.settingsSet);
            setShowSaveButton(false);
        }
    }, [setDomain]);

    const getAllProducts = useCallback(async () => {
        const response = await Domain.getAllProductsByDomain(Utils.getDomain());
        if (!response.error) {
            setProducts(response.data);
        }
    }, [setProducts]);

    const onSubmit = useCallback(async ({ aiPublicKey }) => {
        const response = await Domain.updateDomain(id, { aiPublicKey });
        if (!response.error) {
            setShowSaveButton(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            getDomain(id);
            getAllProducts();
        }
    }, [id, getDomain, getAllProducts]);

    useEffect(() => {
        if (showSaveButton) {
            PubSub.publish("has-unsaved-changes");
        } else {
            PubSub.publish("no-unsaved-changes");
        }
    }, [showSaveButton]);

    const renderProductList = useMemo(() => {
        return (
            <div className="mt-3">
                <label htmlFor="products" className="form-label h6">{translate("OTM_PRODUCTS")}</label>

                <div className="row mt-n2">
                    {products.map(product => (
                        <div className="col-12 col-lg-6 col-xl-4 mt-2" key={product._id}>
                            <div className="form-check form-switch" id="products">
                                <Toggle
                                    id={product._id}
                                    defaultState={domain.products && domain.products.indexOf(product._id) > -1}
                                    disabled={true}
                                />
                                <label className="form-check-label" htmlFor={product._id}>
                                    {product.name}
                                </label>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }, [products, domain, translate]);

    return <div className="p-0 mt-3 d-flex flex-column overflow-auto">
        <Formik initialValues={{
            aiPublicKey: domain.aiPublicKey || "",
        }}
            onSubmit={onSubmit}
            enableReinitialize>
            <Form onChange={() => { setShowSaveButton(true) }}>
                <div className="card mb-3">
                    <div className="card-header border-bottom">
                        <Toolbar
                            heading={translate("OTM_DOMAIN_DETAILS")}
                        />
                    </div>
                    <div className="card-body p-0 px-3 mb-3">
                        {renderProductList}
                    </div>
                </div>

                {(user.adminForDomains.includes(id) || user.appAdmin) && <div className="card d-flex flex-column mb-3">
                    <div className="card-header border-bottom">
                        <Toolbar
                            buttons={[
                                {
                                    name: "Save",
                                    title: translate("OTM_SAVE"),
                                    type: "submit",
                                    hidden: !showSaveButton
                                }
                            ]}
                            heading={translate("OTM_AI_CONFIG")}
                        />
                    </div>
                    <div className="card-body p-0 px-3 mb-3">
                        <div className="row">
                            <TextBox css="col-12 col-lg-6 mt-3" label={translate("OTM_AI_PUBLIC_KEY")} type="text" name="aiPublicKey" disabled={!permissions.update} />
                        </div>

                    </div>
                </div>}
            </Form>
        </Formik>

        <div className="card d-flex flex-column overflow-hidden" style={{ minHeight: '250px' }}>
            {
                settingsSetId &&
                <SettingsView
                    showCancel={false}
                    settingsSetId={settingsSetId}
                    permissions={permissions}
                    context={{ type: 'domain', objectId: domain._id }}
                />
            }
        </div>
    </div>;
};

export default DomainDetails;